import { Injectable } from '@angular/core';
import { Response } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/do';

import {
  SkillDepartmentLearningProgressResponse,
  SkillDepartmentProgressResponse,
  SkillSpecializationListItem,
  SkillSpecializationResponse,
  SkillProgressResponse,
  SkillScoreStatusResponse,
  SkillTrainingProgressResponse,
  AiChatMessage,
  AiChatMessageResponse,
  SkillTestGenerateResponse,
  SkillTestResultResponse,
  SkillTestSubmitRequest,
  SkillPracticeTaskResponse,
  SkillLearningGeneralSettingsResponse,
  SkillPracticeTaskType,
  LearningTreeSpecialization,
  GroupedSpecialization,
  SkillLearningErrorResponse,
} from '../models/skill-learning.model';
import {
  SKILL_LEARNING_EMPTY_TEXT,
  SKILL_LEARNING_EMPTY_COUNT,
  SKILL_CONFIGURATION_ERROR,
  SKILL_HTTP_STATUS,
  SKILL_LEARNING_LEVEL_COUNT,
  SKILL_PRACTICE_TASK_TYPE,
} from '../options/skill-learning.option';
import { SkillSpecializationApiService } from './skill-specialization-api.service';
import { SkillProgressApiService } from './skill-progress-api.service';
import { SkillChatApiService } from './skill-chat-api.service';
import { SkillTestApiService } from './skill-test-api.service';
import { SkillPracticeApiService } from './skill-practice-api.service';
import { SkillSettingsApiService } from './skill-settings-api.service';
import { prioritizeLearningGroups } from './learning-tree-order';

@Injectable()
export class LearningFacadeService {

  private progressLoaded$ = new Subject<SkillProgressResponse>();
  private readonly levelActionError$ = new Subject<SkillLearningErrorResponse>();

  readonly onProgressLoaded$: Observable<SkillProgressResponse> = this.progressLoaded$.asObservable();
  readonly onLevelActionError$: Observable<SkillLearningErrorResponse> =
    this.levelActionError$.asObservable();

  constructor(
    private specializationApi: SkillSpecializationApiService,
    private progressApi: SkillProgressApiService,
    private chatApi: SkillChatApiService,
    private testApi: SkillTestApiService,
    private practiceApi: SkillPracticeApiService,
    private settingsApi: SkillSettingsApiService,
  ) {}

  fetchSpecializationsGrouped(): Observable<GroupedSpecialization[]> {
    return Observable
      .forkJoin(
        this.progressApi.fetchDepartmentLearningProgress(),
        this.specializationApi.fetchAvailableSpecializations(),
      )
      .map(result => this.mapLearningTreeGroups(result[0], result[1]));
  }

  fetchScoreStatus(): Observable<SkillScoreStatusResponse> {
    return this.progressApi.fetchScoreStatus();
  }

  fetchSpecializationDetails(id: string): Observable<SkillSpecializationResponse> {
    return this.specializationApi.fetchSpecializationById(id);
  }

  fetchUserProgress(specializationId: string): Observable<SkillProgressResponse> {
    return this.progressApi.fetchUserProgress(specializationId)
      .do(progress => this.progressLoaded$.next(progress));
  }

  fetchGeneralSettings(): Observable<SkillLearningGeneralSettingsResponse> {
    return this.settingsApi.fetchGeneralSettings();
  }

  toggleTopic(specializationId: string, topicIndex: number): Observable<void> {
    return this.progressApi.toggleTopic(specializationId, topicIndex);
  }

  fetchChatHistory(specializationId: string): Observable<AiChatMessage[]> {
    return this.chatApi.fetchChatHistory(specializationId);
  }

  sendChatMessage(specializationId: string, message: string): Observable<AiChatMessageResponse> {
    const request = { message };
    return this.chatApi.sendMessage(specializationId, request);
  }

  clearChat(specializationId: string): Observable<void> {
    return this.chatApi.clearChat(specializationId);
  }

  generateTest(
    specializationId: string,
    level: number = SKILL_LEARNING_LEVEL_COUNT.ONE,
  ): Observable<SkillTestGenerateResponse> {
    return this.testApi.generateTest(specializationId, level)
      .do(undefined, error => this.notifyLevelActionError(error));
  }

  submitTest(
    specializationId: string,
    request: SkillTestSubmitRequest,
    level: number = SKILL_LEARNING_LEVEL_COUNT.ONE,
  ): Observable<SkillTestResultResponse> {
    return this.testApi.submitTest(specializationId, request, level)
      .do(undefined, error => this.notifyLevelActionError(error));
  }

  getTestCooldown(
    specializationId: string,
    level: number = SKILL_LEARNING_LEVEL_COUNT.ONE,
  ): Observable<number> {
    return this.testApi.fetchTestCooldownSeconds(specializationId, level)
      .do(undefined, error => this.notifyLevelActionError(error));
  }

  startPracticeLevel(
    specializationId: string,
    level: number,
    taskType: SkillPracticeTaskType = SKILL_PRACTICE_TASK_TYPE.PRACTICE,
  ): Observable<SkillPracticeTaskResponse> {
    return this.practiceApi.startPracticeLevel(specializationId, level, taskType)
      .do(undefined, error => this.notifyLevelActionError(error));
  }

  getPracticeTaskStatus(
    specializationId: string,
    level: number,
    taskType: SkillPracticeTaskType = SKILL_PRACTICE_TASK_TYPE.PRACTICE,
  ): Observable<SkillPracticeTaskResponse> {
    return this.practiceApi.fetchPracticeTask(specializationId, level, taskType)
      .do(undefined, error => this.notifyLevelActionError(error));
  }

  buildDocumentDownloadUrl(specializationId: string, level: number): string {
    return this.specializationApi.buildLevelDocumentDownloadUrl(specializationId, level);
  }

  resolveCurrentLevel(progress: SkillProgressResponse): number {
    if (!progress) {
      return 0;
    }
    return progress.currentLevel || 0;
  }

  isLevel1Passed(progress: SkillProgressResponse): boolean {
    return progress && progress.currentLevel >= 1;
  }

  isLevel1SelfTestPassed(progress: SkillProgressResponse): boolean {
    return !!progress && (!!progress.level1SelfTestPassed || !!progress.testPassedAt);
  }

  isLevel2Passed(progress: SkillProgressResponse): boolean {
    return progress && progress.currentLevel >= 2;
  }

  private mapLearningTreeGroups(
    progress: SkillDepartmentLearningProgressResponse,
    specializations: SkillSpecializationListItem[],
  ): GroupedSpecialization[] {
    const specializationMap = this.buildSpecializationMap(specializations);
    const groups = progress.departments.map(department => this.mapLearningTreeGroup(department, specializationMap));
    return prioritizeLearningGroups(groups);
  }

  private mapLearningTreeGroup(
    department: SkillDepartmentProgressResponse, specializationMap: Map<string, SkillSpecializationListItem>,
  ): GroupedSpecialization {
    return {
      id: department.groupId,
      name: department.groupName,
      nameTranslation: department.groupNameTranslation,
      departmentScope: department.departmentScope,
      group: {
        id: department.groupId,
        name: department.groupName,
        nameTranslation: department.groupNameTranslation,
      },
      completedTrainings: department.completedTrainings,
      totalTrainings: department.totalTrainings,
      specializations: department.trainings.map(training =>
        this.mapLearningTreeSpecialization(training, specializationMap)),
    };
  }

  private buildSpecializationMap(
    specializations: SkillSpecializationListItem[],
  ): Map<string, SkillSpecializationListItem> {
    const specializationMap = new Map<string, SkillSpecializationListItem>();
    specializations.forEach(spec => specializationMap.set(spec.id, spec));
    return specializationMap;
  }

  private mapLearningTreeSpecialization(
    training: SkillTrainingProgressResponse, specializationMap: Map<string, SkillSpecializationListItem>,
  ): LearningTreeSpecialization {
    const specialization = specializationMap.get(training.specializationId);
    const totalLevels = specialization && specialization.publishedLevelCount != null
      ? Math.min(training.totalLevels, specialization.publishedLevelCount) : training.totalLevels;
    return Object.assign({
      id: training.specializationId,
      name: training.specializationName,
      nameTranslation: training.specializationNameTranslation,
      description: specialization ? specialization.description || SKILL_LEARNING_EMPTY_TEXT
        : SKILL_LEARNING_EMPTY_TEXT,
      descriptionTranslation: specialization && specialization.descriptionTranslation,
      completedLevels: Math.min(training.completedLevels, totalLevels),
      totalLevels: totalLevels,
      completed: training.completedLevels >= totalLevels,
    }, this.mapLearningRequirements(specialization));
  }

  private mapLearningRequirements(specialization: SkillSpecializationListItem): Partial<LearningTreeSpecialization> {
    return {
      effectiveProbationRequiredLevel: specialization
        ? Math.max(specialization.effectiveProbationRequiredLevel || SKILL_LEARNING_EMPTY_COUNT,
          specialization.currentHeadRequiredLevel || SKILL_LEARNING_EMPTY_COUNT) || null : null,
      probationRequiredByCompany: !!specialization && !!specialization.probationRequiredByCompany,
      probationRequiredByDepartment: !!specialization && !!specialization.probationRequiredByDepartment,
      probationRequirementSourceDepartmentIds: specialization ? specialization.probationRequirementSourceDepartmentIds || [] : [],
      inheritedFromDepartmentId: specialization ? specialization.inheritedFromDepartmentId || null : null,
      currentHeadOnly: !!specialization && !!specialization.currentHeadOnly,
      currentHeadRequiredLevel: specialization ? specialization.currentHeadRequiredLevel || null : null,
      futureHeadRequiredLevel: specialization ? specialization.futureHeadRequiredLevel || null : null,
    };
  }

  private notifyLevelActionError(error: Response): void {
    if (!error || !error.json) {
      return;
    }
    try {
      const body: SkillLearningErrorResponse = error.json();
      const shouldNotify = error.status === SKILL_HTTP_STATUS.FORBIDDEN
        || error.status === SKILL_HTTP_STATUS.CONFLICT
        || error.status === SKILL_HTTP_STATUS.UNPROCESSABLE_ENTITY
        || (!!body && body.code === SKILL_CONFIGURATION_ERROR.LEVEL_NOT_AVAILABLE);
      if (body && body.code && shouldNotify) {
        this.levelActionError$.next(body);
      }
    } catch (ignored) {
      return;
    }
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/services/learning-facade.service.ts